import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(0);
  const { clear } = useCart();
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setAmount(data.amount || 0);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      attempts.current += 1;
      if (attempts.current > 12) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md bg-white rounded-[2rem] border border-slate-100 shadow-xl p-10 text-center"
        data-testid="payment-success-page"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-sky-500 mx-auto animate-spin" />
            <h1 className="font-heading font-extrabold text-2xl text-slate-900 mt-6">Vérification du paiement...</h1>
            <p className="text-slate-500 mt-2">Merci de patienter quelques instants.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <span className="inline-grid place-items-center w-20 h-20 rounded-full bg-emerald-100 text-emerald-500 mx-auto">
              <CheckCircle2 className="w-12 h-12" />
            </span>
            <h1 className="font-heading font-black text-3xl text-slate-900 mt-6">Paiement réussi !</h1>
            <p className="text-slate-500 mt-2">
              Merci pour votre commande de <strong>{amount.toFixed(2)} €</strong>. Un email de confirmation vous a été envoyé.
            </p>
            <div className="flex flex-col gap-3 mt-8">
              <Link to="/mon-compte" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 transition-colors" data-testid="success-dashboard">
                Voir mes commandes <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/boutique" className="text-sky-600 font-bold">Continuer mes achats</Link>
            </div>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <span className="inline-grid place-items-center w-20 h-20 rounded-full bg-rose-100 text-rose-500 mx-auto">
              <XCircle className="w-12 h-12" />
            </span>
            <h1 className="font-heading font-extrabold text-2xl text-slate-900 mt-6">
              {status === "timeout" ? "Paiement en cours de traitement" : "Un souci est survenu"}
            </h1>
            <p className="text-slate-500 mt-2">
              {status === "timeout"
                ? "Le paiement prend plus de temps que prévu. Vérifiez vos commandes dans quelques minutes."
                : "Nous n'avons pas pu confirmer le paiement. Contactez-nous si le problème persiste."}
            </p>
            <Link to="/boutique" className="inline-block mt-8 px-7 py-3.5 rounded-full bg-slate-900 text-white font-bold">
              Retour à la boutique
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
